import type { JourneyLeg } from "@/shared/contracts";
import { formatDuration } from "@/shared/time/clock";

/** UTC fields require an explicit offset; never infer a date from the device zone. */
function instant(value: string | null | undefined): number {
  if (!value || !/(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) return NaN;
  return Date.parse(value);
}

export function departureMs(times: JourneyLeg["times"]): number {
  return instant(times.actual_departure_utc ?? times.estimated_departure_utc ?? times.planned_departure_utc);
}

export function arrivalRemaining(times: JourneyLeg["times"], nowMs: number): number {
  return instant(times.actual_arrival_utc ?? times.estimated_arrival_utc ?? times.planned_arrival_utc) - nowMs;
}

/**
 * An estimate is never an arrival fact, including zero/past or unknown estimates.
 * 全站说“还要多久到”只用这一个函数（旅途页、行程卡、地图面板、小窝门上便签；I 10-13 统一，按 CTO-4350 旅途页的说法）：
 * 只说“预计还要…”，不足一分钟按 1 分钟估；预计时间过了说“还在路上，…时间待更新”，读不出说“…时间待更新”，不说“马上就到”。
 * target：到哪儿（如“到家”）；给了就写进句子：“到家预计还要 13 分钟”“还在路上，到家时间待更新”。
 */
export function estimateLeftText(ms: number, target?: string): string {
  const pending = `${target ?? "抵达"}时间待更新`;
  if (!Number.isFinite(ms)) return pending;
  if (ms <= 0) return `还在路上，${pending}`;
  return `${target ?? ""}预计还要 ${formatDuration(Math.max(60_000, ms))}`;
}

/** Keep local calendar date and the actual origin zone together, including midnight. */
export function departureLabel(times: JourneyLeg["times"]): string {
  const ms = departureMs(times);
  if (!Number.isFinite(ms)) return "出发时间待更新";
  let zone = times.origin_timezone;
  let fallback = false;
  try {
    if (!zone?.trim()) throw new RangeError("Missing origin zone");
    new Intl.DateTimeFormat("zh-CN", { timeZone: zone }).format(ms);
  } catch {
    zone = "UTC";
    fallback = true;
  }
  const parts = new Intl.DateTimeFormat("zh-CN", { timeZone: zone, month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZoneName: "longOffset" }).formatToParts(ms);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  const offset = part("timeZoneName").replace(/^GMT/, "UTC");
  return `${part("month")}月${part("day")}日 ${part("hour")}:${part("minute")} · ${offset}${fallback ? "（时区待确认）" : ""}`;
}
